import { Logger, Sdk, getSdk, LogLevel } from '@monterosa-sdk/core';
import { createError, getGlobal, Emitter, subscribe, delay, throttle, checkAvailability, setItem, clear, getItem, removeItem } from '@monterosa-sdk/util';
import { v4 } from 'uuid';
import { fetchListings } from '@monterosa-sdk/interact-kit';

/**
 * @license
 * constants.ts
 * launcher-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-07-15
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
const RESIZE_THROTTLE_TIMEOUT = 25;
/**
 * Duration of the loader fade IN/OUT animation
 */
const LOADER_ANIMATION_DURATION = 750;
/**
 * Final cut off timeout for the loader after which it will be hidden even
 * though Experience UI may ne not yet ready
 */
const LOADER_TIMEOUT = 5000;
/**
 * Bumper timeout during which the loader still visible even though Experience
 * UI may be ready
 *
 * (!) At the moment its value is 0 as loader has a fade in effect what already
 * adds a small bumper delay before Experience is injected on the page
 */
const LOADER_BUMPER_TIMEOUT = 0;
const DEFAULT_HEIGHT = 250;

/**
 * @license
 * logger.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2023-05-23
 * Copyright © 2023 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
const logger = new Logger('@monterosa-sdk/interact-kit');

/**
 * @license
 * public_types.ts
 * launcher-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-07-12
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * A list of possible actions in communications between
 * parent application and child Experience
 *
 * @internal
 */
var Action;
(function (Action) {
    /**
     * Notifies that communication channel is ready and
     * messages can be {@link sendSdkMessage | sent}
     */
    Action["OnReady"] = "onBridgeReady";
    /**
     * Notifies that intrisic size of child Experience has changed
     */
    Action["OnResize"] = "onIntrinsicSizeChanged";
    /**
     * Notifies that UI of child Experience is loaded.
     * When this action is fired the loader will be hidden
     */
    Action["OnUILoaded"] = "onUILoaded";
    /**
     * Notifies child Experience about the request to load more data
     */
    Action["OnMoreDataRequested"] = "onMoreDataRequested";
})(Action || (Action = {}));
/**
 * @internal
 */
var Source;
(function (Source) {
    Source["Sdk"] = "sdk";
    Source["User"] = "user";
})(Source || (Source = {}));

/**
 * @license
 * types.ts
 * launcher-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-01-19
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * Reserved query parameter names for controlling the behaviour of Experience.
 */
var QueryParam;
(function (QueryParam) {
    /**
     * Represents the host of the application. This parameter is added by Studio
     * and the embed URL already contains it.
     */
    QueryParam["Host"] = "h";
    /**
     * Represents the Project Id of the application. This parameter is added by
     * Studio and the embed URL already contains it.
     */
    QueryParam["Project"] = "p";
    /**
     * Represents the Event Id of the application. This parameter is added to the
     * experience URL when the eventId is provided in the experience config.
     */
    QueryParam["Event"] = "e";
    /**
     * Used to identify the unique ID of the communication bridge between
     * the parent application and the child experience.
     */
    QueryParam["BridgeId"] = "micBridgeId";
    /**
     * Determines whether the header and footer views of the application should be
     * hidden. This parameter can be used to control the visibility of the
     * application's header and footer components.
     */
    QueryParam["HideHeaderAndFooter"] = "micHideHeaderAndFooter";
    /**
     * Determines whether autoresize is enabled for the application. When autoresize
     * is enabled, the application's height will adjust automatically based on its
     * content.
     */
    QueryParam["AutoresizesHeight"] = "micAutoresizesHeight";
})(QueryParam || (QueryParam = {}));

/**
 * @license
 * types.ts
 * launcher-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2024-01-02
 * Copyright © 2024 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * Defines a set of error codes that may be encountered when using the
 * Launcher Kit bridge
 *
 * @example
 * ```javascript
 * try {
 *   // some code that uses the LauncherKit's bridge
 * } catch (err) {
 *   if (err.code === BridgeError.InvalidRequestTimeoutError) {
 *     // handle invalid request timeout error
 *   } else {
 *     // handle other error types
 *   }
 * }
 * ```
 *
 * @remarks
 * - The `BridgeError` enum provides a convenient way to handle errors
 *   encountered when using the `LauncherKit` module. By checking the code
 *   property of the caught error against the values of the enum, the error
 *   type can be determined and appropriate action taken.
 *
 * - The `BridgeError` enum is not intended to be instantiated or extended.
 */
var BridgeError;
(function (BridgeError) {
    /**
     * Indicates an error occurred due to an invalid timeout value being provided.
     * This error is thrown when the specified timeout is not a positive number.
     */
    BridgeError["InvalidRequestTimeoutError"] = "invalid_request_timeout_error";
})(BridgeError || (BridgeError = {}));
const BridgeErrorMessages = {
    [BridgeError.InvalidRequestTimeoutError]: () => 'Request timeout must be greater than 0',
};

/**
 * @license
 * config.ts
 * launcher-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2024-01-02
 * Copyright © 2024 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * @internal
 */
const Config = {
    requestTimeout: 20000,
};
/**
 * Sets a new timeout value for requests (default is 20_000 mseconds).
 *
 * This function updates the request timeout in the application's configuration.
 * It ensures that the new timeout value is a positive number, and throws
 * an error if the value is non-positive.
 *
 * @param newTimeout - The new timeout value in milliseconds. Must be
 *   a positive number.
 *
 * @throws
 * Throws {@link BridgeError | BridgeError.InvalidRequestTimeoutError}
 * if `newTimeout` is less than or equal to 0.
 *
 * @example
 * ```javascript
 * // Set the request timeout to 3000 milliseconds
 * setRequestTimeout(3000);
 * ```
 */
function setRequestTimeout(newTimeout) {
    if (newTimeout <= 0) {
        throw createError(BridgeError.InvalidRequestTimeoutError, BridgeErrorMessages);
    }
    Config.requestTimeout = newTimeout;
}

/**
 * @license
 * helpers.ts
 * launcher-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-07-12
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
function isMessage(message) {
    return (message instanceof Object &&
        Object.prototype.hasOwnProperty.call(message, 'bridgeId'));
}

/**
 * @license
 * sideeffect.ts
 * launcher-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-07-12
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
var _a$1, _b$1, _c$1, _d$1;
var _e, _f, _g;
const globals$2 = getGlobal();
const receiveMessage = (message) => {
    if (!isMessage(message)) {
        // data does not match message format
        return;
    }
    globals$2.monterosaSdk.emitter.emit('message', message);
};
function handleWindowMessage({ data }) {
    try {
        if (typeof data !== 'string') {
            // ignore non string data
            return;
        }
        const message = JSON.parse(data);
        receiveMessage(message);
    }
    catch (err) {
        if (err instanceof Error) {
            console.error(err.message);
        }
    }
}
/**
 * Since this code operates as a side effect while updating the global space,
 * we must be very careful because it may run twice, for example, when two apps
 * using the SDK run on the same page. Additionally, the global namespace may
 * already be updated by the Android or iOS SDKs.
 */
// The Monterosa SDK namespace may already exist, either because it was created
// by a native SDK or by another web app using the JS SDK from the same scope.
(_a$1 = globals$2.monterosaSdk) !== null && _a$1 !== void 0 ? _a$1 : (globals$2.monterosaSdk = {
    initialised: false,
    emitter: new Emitter(),
    receiveMessage,
});
// Each of nullish coalescing assignments will only be applied when
// the Monterosa SDK namespace exists at that moment and one of its properties
// is not defined.
(_b$1 = (_e = globals$2.monterosaSdk).initialised) !== null && _b$1 !== void 0 ? _b$1 : (_e.initialised = false);
(_c$1 = (_f = globals$2.monterosaSdk).emitter) !== null && _c$1 !== void 0 ? _c$1 : (_f.emitter = new Emitter());
(_d$1 = (_g = globals$2.monterosaSdk).receiveMessage) !== null && _d$1 !== void 0 ? _d$1 : (_g.receiveMessage = receiveMessage);
if (!globals$2.monterosaSdk.initialised) {
    // Subscribe to the message only once for each app that uses the SDK on this page.
    if (typeof globals$2.addEventListener !== 'undefined') {
        globals$2.addEventListener('message', handleWindowMessage);
    }
    globals$2.monterosaSdk.initialised = true;
}

/**
 * @license
 * constants.ts
 * launcher-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-07-12
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
var _a, _b, _c, _d;
const globals$1 = getGlobal();
/**
 * @internal
 */
const IFRAME_ID_PREFIX = 'micBridge';
const IS_IOS = !!((_c = (_b = (_a = globals$1.webkit) === null || _a === void 0 ? void 0 : _a.messageHandlers) === null || _b === void 0 ? void 0 : _b.monterosaSdk) === null || _c === void 0 ? void 0 : _c.postMessage);
const IS_ANDROID = !!((_d = globals$1.monterosaSdk) === null || _d === void 0 ? void 0 : _d.postMessage);
const IS_WEB = globals$1.self !== globals$1.parent;
/**
 * @internal
 */
const VERSION$1 = '1.0.0';

/**
 * @license
 * bridge_impl.ts
 * launcher-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-07-12
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
const globals = getGlobal();
/**
 * @internal
 */
class BridgeImpl extends Emitter {
    constructor(id = v4()) {
        super();
        this.id = id;
        globals.monterosaSdk.emitter.on('message', this.handleMessage.bind(this));
    }
    static isMessage(message) {
        return (message instanceof Object &&
            Object.prototype.hasOwnProperty.call(message, 'bridgeId') &&
            Object.prototype.hasOwnProperty.call(message, 'action'));
    }
    get iFrameId() {
        return `${IFRAME_ID_PREFIX}-${this.id}`;
    }
    get iFrameSelector() {
        return `iframe#${this.iFrameId}`;
    }
    get childIFrame() {
        return document.querySelector(this.iFrameSelector);
    }
    handleMessage(message) {
        if (message.bridgeId === this.id) {
            logger.log(`Received a ${message.respondingTo === null ? 'message' : 'response'}`, message);
            this.emit('message', message);
        }
    }
    createMessage(action, payload, sourceName, respondingTo = null) {
        return {
            id: v4(),
            respondingTo,
            action,
            sourceName,
            bridgeId: this.id,
            payload,
            version: VERSION$1,
            timestamp: Date.now(),
        };
    }
    postMessage(message) {
        var _a, _b, _c, _d;
        const json = JSON.stringify(message);
        if (IS_IOS) {
            (_b = (_a = globals.webkit) === null || _a === void 0 ? void 0 : _a.messageHandlers) === null || _b === void 0 ? void 0 : _b.monterosaSdk.postMessage(json);
        }
        if (IS_ANDROID) {
            if ((_c = globals.monterosaSdk) === null || _c === void 0 ? void 0 : _c.postMessage) {
                globals.monterosaSdk.postMessage(json);
            }
        }
        if (IS_WEB) {
            globals.parent.postMessage(json, '*');
        }
        if (this.childIFrame) {
            (_d = this.childIFrame.contentWindow) === null || _d === void 0 ? void 0 : _d.postMessage(json, '*');
        }
    }
    send(action, payload = {}, sourceName = Source.Sdk, respondingTo) {
        const message = this.createMessage(action, payload, sourceName, respondingTo);
        logger.log(`Sending a ${message.respondingTo === null ? 'message' : 'response'}`, message);
        this.postMessage(message);
        return message;
    }
    async request(action, payload = {}, timeout = Config.requestTimeout, sourceName = Source.Sdk) {
        let timeoutRef;
        let handler;
        const message = this.createMessage(action, payload, sourceName);
        logger.log('Sending a request', message);
        /**
         * Start the timeout, when it finishes it should reject Promise.race below
         */
        const countdown = new Promise((_, reject) => {
            timeoutRef = setTimeout(reject, timeout);
        });
        /**
         * Start the request and wait for the message with the respondingTo
         * equal to message id we sent
         */
        const request = new Promise((resolve) => {
            handler = (responseMessage) => {
                if (responseMessage.respondingTo === message.id) {
                    resolve(responseMessage);
                }
            };
            globals.monterosaSdk.emitter.on('message', handler);
            this.postMessage(message);
        });
        /**
         * Start race between timeout and request
         *   - if timeout wins the promise will be rejected
         *   - if request wins then Message will be resolved
         */
        return (Promise.race([countdown, request])
            /**
             * As the matter of clean up we need to clear timeout id
             * and unsubscribe from the message event
             */
            .finally(() => {
            clearTimeout(timeoutRef);
            globals.monterosaSdk.emitter.off('message', handler);
        }));
    }
}

/**
 * @license
 * api.ts
 * launcher-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-07-12
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
let parentBridge;
const bridges = new Map();
/**
 * @internal
 */
function getBridge(id) {
    if (bridges.has(id)) {
        return bridges.get(id);
    }
    const bridge = new BridgeImpl();
    bridges.set(id, bridge);
    return bridge;
}
/**
 * @internal
 */
function getParentBridge() {
    if (typeof window === 'undefined') {
        return null;
    }
    if (parentBridge !== undefined) {
        return parentBridge;
    }
    const url = new URL(window.location.href);
    const bridgeId = url.searchParams.get(QueryParam.BridgeId);
    if (bridgeId === null) {
        return null;
    }
    parentBridge = new BridgeImpl(bridgeId);
    return parentBridge;
}
/**
 * @internal
 */
function sendSdkMessage(bridged, action, payload = {}) {
    return bridged.bridge.send(action, payload, Source.Sdk);
}
/**
 * This function allows to send a simple message with action name and payload to
 * a recipient which can be either parent application (from Experience to parent
 * application) or child Experience (from parent page to Experience).
 *
 * @remarks
 * Usage example in parent application:
 *
 * @example
 * ```typescript
 * const experience = getExperience();
 *
 * // message to Experience can be sent only when its ready
 * onUILoaded(experience, async () => {
 *   sendMessage(
 *     experience,
 *     'my_action',
 *     { key: 'value' }
 *   );
 * });
 *
 * embed(experience);
 * ```
 *
 * Usage example in child Experience:
 *
 * @example
 * ```typescript
 * const parentApp = getParentApplication();
 *
 * if (parentApp !== null) {
 *   sendMessage(
 *     parentApp,
 *     'my_action',
 *     { key: 'value' }
 *   );
 * }
 * ```
 *
 * @param bridged - Instance of either {@link ParentApplication} or {@link Experience}
 * @param action - Arbitrary action name that defines purpose of the message
 * @param payload - A key-value object that is sent in a message
 * @returns Returns {@link Message | message}
 */
function sendMessage(bridged, action, payload = {}) {
    return bridged.bridge.send(action, payload, Source.User);
}
/**
 * @internal
 */
function sendSdkRequest(bridged, action, payload = {}, timeout = Config.requestTimeout) {
    return bridged.bridge.request(action, payload, timeout, Source.Sdk);
}
/**
 * This function allows to send a request with action name and payload to
 * a recipient which can be either parent application (from Experience to parent
 * application) or child Experience (from parent page to Experience). It is similar
 * to {@link sendMessage} with only one difference is that it returns a Promise which
 * resolves if the recipient response with {@link respondToMessage}. Otherwise it
 * will rejects after a certain timeout.
 *
 * @remarks
 * Usage example in parent application:
 *
 * @example
 * ```typescript
 * const experience = getExperience();
 *
 * // request to Experience can be sent only when its ready
 * onUILoaded(experience, async () => {
 *   const response = await sendRequest(
 *     experience,
 *     'my_action',
 *     { key: 'value' }
 *   );
 *
 *   console.log(response);
 * });
 *
 * embed(experience);
 * ```
 *
 * Usage example in child Experience:
 *
 * @example
 * ```typescript
 * const parentApp = getParentApplication();
 *
 * // parent application can be null if Experience is running stand alone
 * if (parentApp !== null) {
 *   const response = await sendRequest(
 *     parentApp,
 *     'my_action',
 *     { key: 'value' }
 *   );
 *
 *   console.log(response);
 * }
 * ```
 *
 * @param bridged - Instance of either {@link ParentApplication} or {@link Experience}
 * @param action - Arbitrary action name that defines purpose of the message
 * @param payload - A key-value object that is sent as in request
 * @param timeout - Configurable request timeout, if there is no response by
 * the end of this timeout then returned Promise will be rejected.
 */
function sendRequest(bridged, action, payload = {}, timeout = Config.requestTimeout) {
    return bridged.bridge.request(action, payload, timeout, Source.User);
}
/**
 * @internal
 */
function respondToSdkMessage(bridged, message, payload = {}) {
    bridged.bridge.send(message.action, payload, Source.Sdk, message.id);
}
/**
 * Respond to a received message. It is used to reply to a
 * {@link sendRequest | user request}.
 *
 * @param bridged - Instance of either {@link ParentApplication} or {@link Experience}
 * @param message - {@link Message | Message} to respond to
 * @param payload - A key-value object that is sent as in response
 */
function respondToMessage(bridged, message, payload = {}) {
    bridged.bridge.send(message.action, payload, Source.User, message.id);
}
function onMessageFunc(bridged, source, callback) {
    return subscribe(bridged.bridge, 'message', (message) => {
        if (message.sourceName === source) {
            callback(message);
        }
    });
}
/**
 * @internal
 */
function onSdkMessage(bridged, callback) {
    return onMessageFunc(bridged, Source.Sdk, callback);
}
/**
 * Adds an observer for when user message is received
 *
 * @param bridged - Instance of either {@link ParentApplication} or {@link Experience}
 * @param callback - The callback that is triggered when user message is received
 * @returns The unsubscribe function. When it's called,
 * the observer will be removed and user messages will no longer be received
 */
function onMessage(bridged, callback) {
    return onMessageFunc(bridged, Source.User, callback);
}

/**
 * @license
 * experience_impl.ts
 * launcher-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-01-19
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * @internal
 */
class ExperienceImpl {
    constructor(sdk, config) {
        this.sdk = sdk;
        this._parameters = {};
        this._config = config;
        this.bridge = getBridge(v4());
        if (config.parameters !== undefined) {
            const reserved = Object.values(QueryParam);
            const ignored = [];
            Object.entries(config.parameters).forEach(([key, value]) => {
                if (reserved.includes(key)) {
                    ignored.push(key);
                }
                else {
                    this._parameters[key] = value;
                }
            });
            if (ignored.length === 1) {
                console.warn(`Parameter "${ignored[0]}" ignored as it matches reserved words`);
            }
            if (ignored.length > 1) {
                console.warn(`Parameters "${ignored.join('", "')}" ignored as they match reserved words`);
            }
        }
    }
    get config() {
        var _a, _b, _c, _d;
        return Object.assign(this._config, {
            autoresizesHeight: (_a = this._config.autoresizesHeight) !== null && _a !== void 0 ? _a : false,
            hidesHeadersAndFooters: (_b = this._config.hidesHeadersAndFooters) !== null && _b !== void 0 ? _b : true,
            supportsLoadingState: (_c = this._config.supportsLoadingState) !== null && _c !== void 0 ? _c : true,
            experienceUrl: (_d = this._config.experienceUrl) !== null && _d !== void 0 ? _d : null,
        });
    }
    async getEmbedUrl() {
        if (this.embedUrl) {
            return Promise.resolve(this.embedUrl);
        }
        const { options: { host, projectId }, } = this.sdk;
        const listings = await fetchListings(host, projectId);
        this.embedUrl = listings.project.embed;
        return this.embedUrl;
    }
    async getUrl() {
        const embedUrl = await this.getEmbedUrl();
        // sanitise and create an object from an url
        let url = new URL(ExperienceImpl.sanitiseUrl(embedUrl));
        // override host and project with those that are set in SDK instance
        url.searchParams.set(QueryParam.Host, this.sdk.options.host);
        url.searchParams.set(QueryParam.Project, this.sdk.options.projectId);
        // if custom experienceUrl is set:
        // * take experienceUrl as the base
        // * search params from experienceUrl have priority
        if (this.config.experienceUrl !== null) {
            // sanitise and create an object from a custom url
            const customUrl = new URL(ExperienceImpl.sanitiseUrl(this.config.experienceUrl));
            // apply embedUrl query parameters if they don't exist in experienceUrl
            Array.from(url.searchParams.entries())
                .filter(([key]) => !customUrl.searchParams.has(key))
                .forEach(([key, value]) => customUrl.searchParams.set(key, value));
            url = customUrl;
        }
        // Prepare a final list of query parameters
        const queryParameters = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, (this.config.host !== undefined && {
            [QueryParam.Host]: this.config.host,
        })), (this.config.projectId !== undefined && {
            [QueryParam.Project]: this.config.projectId,
        })), (this.config.eventId !== undefined && {
            [QueryParam.Event]: this.config.eventId,
        })), { [QueryParam.BridgeId]: this.bridge.id, [QueryParam.HideHeaderAndFooter]: this.config.hidesHeadersAndFooters
                ? '1'
                : '0', [QueryParam.AutoresizesHeight]: this.config.autoresizesHeight ? '1' : '0' }), this._parameters);
        Object.entries(queryParameters).forEach(([key, value]) => {
            url.searchParams.set(key, value);
        });
        return url.href;
    }
    static sanitiseUrl(url, options) {
        const stripReservedParameters = (options === null || options === void 0 ? void 0 : options.stripReservedParameters) || 'mic';
        // Ensure the URL includes a protocol.
        if (/^http/.test(url) === false) {
            // If none are found, use the same as the document.
            url = `${document.location.protocol}${url}`;
        }
        const urlObj = new URL(url);
        Object.values(QueryParam)
            .filter((key) => {
            if (stripReservedParameters === 'mic') {
                return key.startsWith('mic');
            }
            return key;
        })
            .forEach((key) => urlObj.searchParams.delete(key));
        return urlObj.href;
    }
}

/**
 * @license
 * loader.ts
 * launcher-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-08-04
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/* eslint-enable  */
function getLoadingTemplate() {
    return `
    <div>
      <style type="text/css">
        .__mic-loader {
          position: absolute;
          width: 100%;
          height: 100%;
          z-index: 9;
          box-sizing: border-box;
          text-align: center;
          background: #fff;
        }

        .__mic-loader__container {
          padding: 20px 0;
          gap: 20px;
          min-height: ${DEFAULT_HEIGHT}px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }

        .__mic-loader__spinner {
          position: relative;
          width: 10px;
          height: 10px;
          border-radius: 5px;
          background-color: #0b0f1c;
          color: #0b0f1c;
          animation: mic-loader__animation 1s infinite linear alternate;
          animation-delay: 0.5s;          
        }

        .__mic-loader__spinner::before,
        .__mic-loader__spinner::after {
          content: "";
          display: inline-block;
          position: absolute;
          top: 0;
        }

        .__mic-loader__spinner::before {
          left: -15px;
          width: 10px;
          height: 10px;
          border-radius: 5px;
          background-color: #0b0f1c;
          color: #0b0f1c;
          animation: mic-loader__animation 1s infinite alternate;
          animation-delay: 0s;
        }

        .__mic-loader__spinner::after {
          left: 15px;
          width: 10px;
          height: 10px;
          border-radius: 5px;
          background-color: #0b0f1c;
          color: #0b0f1c;
          animation: mic-loader__animation 1s infinite alternate;
          animation-delay: 1s;
        }

        @keyframes mic-loader__animation {
          0% {
            background-color: #0b0f1c;
          }
          50%, 100% {
            background-color: rgba(11, 15, 28, 0.2);
          }
        }
      </style>
      <div class="__mic-loader">
        <div class="__mic-loader__container">
          <div class="__mic-loader__spinner"></div>
        </div>
      </div>
    </div>
  `;
}
function embed$1(container, loadingTemplate = getLoadingTemplate) {
    /**
     * Return existing element if it exists
     */
    let loaderElement = container.querySelector('[data-role=loader]');
    if (loaderElement !== null) {
        return loaderElement;
    }
    /**
     * Stash and apply some custom styles to container element
     */
    stashStyles(container, ['minHeight', 'position']);
    container.style.minHeight = `${DEFAULT_HEIGHT}px`;
    /**
     * Inject loader element built from the html markup template
     */
    const templateElement = document.createElement('template');
    templateElement.innerHTML = loadingTemplate().trim();
    loaderElement = container.appendChild(templateElement.content.firstElementChild);
    /**
     * Setting custom data attribute to be able to distinguish element along
     * other injected html elements if any
     */
    loaderElement.setAttribute('data-role', 'loader');
    return loaderElement;
}
function unmount$1(container) {
    var _a;
    const loaderElement = container.querySelector('[data-role=loader]');
    if (loaderElement === null) {
        return;
    }
    (_a = loaderElement.parentElement) === null || _a === void 0 ? void 0 : _a.removeChild(loaderElement);
    unstashStyles(container);
}
async function show(container, loadingTemplate = getLoadingTemplate) {
    const loaderElement = embed$1(container, loadingTemplate);
    const { position } = getComputedStyle(container);
    /**
     * In order to properly position the loader,
     * the container's position should be anything but 'static'.
     */
    if (position === 'static') {
        container.style.position = 'relative';
    }
    /**
     * Setting styles to the loader for a nice fade in animation
     */
    loaderElement.style.opacity = '0';
    /**
     * Kicking fade animation in.
     */
    setTimeout(() => {
        loaderElement.style.transition = `all ${LOADER_ANIMATION_DURATION}ms`;
        loaderElement.style.opacity = '1';
    }, 0);
    /**
     * Wait until the animation is finished. We do not use "transitionend" event
     * here as it will just make the code more bloated and unnecessary complex.
     */
    await delay(LOADER_ANIMATION_DURATION);
}
async function hide(container) {
    const loaderElement = container.querySelector('[data-role=loader]');
    if (loaderElement === null) {
        return;
    }
    /**
     * Kicking fade out animation.
     */
    setTimeout(() => {
        loaderElement.style.opacity = '0';
    }, 0);
    /**
     * Wait until the animation is finished. We do not use "transitionend" event
     * here as it will just make the code more bloated and unnecessary complex.
     */
    await delay(LOADER_ANIMATION_DURATION);
    unstashStyles(container);
    unmount$1(container);
}
function stashStyles(element, props) {
    const styles = getComputedStyle(element);
    const stash = props
        .map((prop) => `${String(prop)}=${styles[prop]}`)
        .join(';');
    element.setAttribute('data-stash', stash);
}
function unstashStyles(element) {
    const stash = element.getAttribute('data-stash');
    if (stash === null) {
        return;
    }
    const attributes = stash
        .split(';')
        .map((attr) => attr.split('='));
    for (const [attr, value] of attributes) {
        element.style[attr] = value;
    }
    element.removeAttribute('data-stash');
}

var version = "0.17.1";

/**
 * @license
 * api.ts
 * launcher-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-02-15
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * The current SDK version.
 *
 * @internal
 */
const VERSION = version;
const integrations = new Map();
const embedHooks = [];
/**
 * Creates an iframe with the provided parameters.
 *
 * @param url Url of the web app.
 *
 * @private
 */
function createIFrame(url, options = {}) {
    const iframe = document.createElement('iframe');
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = '0';
    iframe.style.display = 'block';
    iframe.style.boxSizing = 'border-box';
    iframe.setAttribute('src', url);
    if (options.id !== undefined) {
        iframe.setAttribute('id', options.id);
    }
    if (options.allow !== undefined) {
        iframe.setAttribute('allow', options.allow);
    }
    if (options.allowFullScreen === true) {
        iframe.setAttribute('allowfullscreen', '');
    }
    /**
     * Though scrolling attribute is deprecated we still have to rely on it as some
     * browsers (e.g. Chrome, as of now version 103) just ignores overflow: hidden.
     * Hence we are setting scrolling to no in order to eliminate scroll and calculate
     * width of the child Experience correctly. If scroll persists it appears on each
     * content change what leads to incorrect width calculation.
     *
     * Later we will look into alternatives such as iframeless Experience embed
     */
    if (!options.scrolling) {
        iframe.style.overflow = 'hidden';
        iframe.setAttribute('scrolling', 'no');
    }
    return iframe;
}
function concealIFrame(iframe) {
    iframe.style.opacity = '0';
}
function revealIFrame(iframe) {
    iframe.style.opacity = '1';
}
function isSdk(value) {
    return value instanceof Sdk;
}
function getExperience(sdkOrConfig, config) {
    let sdk;
    let experienceConfig;
    if (isSdk(sdkOrConfig)) {
        /**
         * Interface: getExperience(sdk, config?)
         */
        sdk = sdkOrConfig;
        if (config !== undefined) {
            experienceConfig = config;
        }
        else {
            experienceConfig = {};
        }
    }
    else if (sdkOrConfig !== undefined) {
        /**
         * Interface: getExperience(config)
         */
        sdk = getSdk();
        experienceConfig = sdkOrConfig;
    }
    else {
        /**
         * Interface: getExperience()
         */
        sdk = getSdk();
        experienceConfig = {};
    }
    const experience = new ExperienceImpl(sdk, experienceConfig);
    return experience;
}
function onResize(experience, container) {
    if (!experience.config.autoresizesHeight) {
        return () => { };
    }
    return onSdkMessage(experience, ({ action, payload }) => {
        if (action === Action.OnResize) {
            /**
             * Container width is maintained the same as it is defined in the parent
             * page to preserve its behaviour. Only the height is updated
             */
            // container.style.width = `${payload.width}px`;
            container.style.height = `${payload.height}px`;
        }
    });
}
function onUILoaded(experience) {
    return new Promise((resolve) => {
        const unsubscribe = onSdkMessage(experience, ({ action }) => {
            if (action === Action.OnUILoaded) {
                unsubscribe();
                resolve();
            }
        });
    });
}
async function experienceReady(experience) {
    /**
     * Bumper delay during which loading state can't be hidden even
     * if Experience loaded earlier than timer reached bumper timeout
     */
    const bumper = delay(LOADER_BUMPER_TIMEOUT);
    /**
     * Final cut off timeout delay after which loading state must
     * be hidden even if Experience is still loading
     */
    const timeout = delay(LOADER_TIMEOUT);
    /**
     * Promisifying OnUILoaded communication bridge event
     */
    const loaded = onUILoaded(experience);
    const hasFullyLoadedExperience = Promise.all([bumper, loaded]);
    const eitherTimeoutOrFullyLoaded = Promise.race([
        timeout,
        hasFullyLoadedExperience,
    ]);
    return eitherTimeoutOrFullyLoaded;
}
/**
 * Embeds web Experience app into iframe. There is only one app can be
 * associated with Experience and it is configured in
 * Monterosa / Interaction Cloud. Please refer the developer guide to get
 * more information on what is app and how to configure it:
 * {@link https://products.monterosa.co/mic/developer-guides/whats-an-app}
 *
 * @example
 * ```javascript
 * const experience = getExperience();
 *
 * embed(experience, 'container-id');
 * ```
 * @param {Experience} - An instance of Experience
 * @param {HTMLElement | string} containerOrId - HTML element instance or
 *     element id where iframe is embedded into.
 *
 * @public
 */
async function embed(experience, containerOrId) {
    const container = containerOrId instanceof HTMLElement
        ? containerOrId
        : document.getElementById(containerOrId);
    if (container === null) {
        throw new Error(`Container element with id "${containerOrId}" doesn't exist in DOM`);
    }
    if (integrations.has(container)) {
        return;
    }
    const controller = new AbortController();
    const hooksUnsubscribe = [];
    integrations.set(container, {
        experience,
        controller,
        hooksUnsubscribe,
    });
    if (experience.config.supportsLoadingState === true) {
        // Although showLoader is an asynchronous function, we execute
        // it synchronously to embed the iframe as quickly as possible.
        show(container, experience.config.loadingTemplate);
    }
    for (const hook of embedHooks) {
        const unsub = hook(experience, container);
        hooksUnsubscribe.push(unsub);
    }
    const url = await experience.getUrl();
    if (controller.signal.aborted) {
        return;
    }
    const iframe = createIFrame(url, {
        id: experience.bridge.iFrameId,
        scrolling: !experience.config.autoresizesHeight,
        allow: experience.config.allow,
        allowFullScreen: experience.config.allowFullScreen,
    });
    container.appendChild(iframe);
    concealIFrame(iframe);
    await experienceReady(experience);
    if (controller.signal.aborted) {
        return;
    }
    revealIFrame(iframe);
    if (experience.config.supportsLoadingState === true) {
        await hide(container);
    }
}
/**
 * Unmounts web Experience app which was previously embedded in the container.
 *
 * @example
 * ```javascript
 * unmount('container-id');
 * ```
 *
 * @param {HTMLElement | string} containerOrId - HTML element instance or
 *     element id where iframe is embedded into.
 *
 * @public
 */
function unmount(containerOrId) {
    const container = containerOrId instanceof HTMLElement
        ? containerOrId
        : document.getElementById(containerOrId);
    if (container === null) {
        throw new Error(`Container element with id "${containerOrId}" doesn't exist in DOM`);
    }
    const integration = integrations.get(container);
    if (!integration) {
        return;
    }
    integration.controller.abort();
    for (const unsub of integration.hooksUnsubscribe) {
        unsub();
    }
    while (container.lastElementChild) {
        container.removeChild(container.lastElementChild);
    }
    integrations.delete(container);
    container.style.height = '';
}
/**
 * Informs the Experience that more data should be loaded and displayed on the UI.
 *
 * @remarks
 * One example is when Experience renders items feed partially. Once a user scrolled
 * to the bottom edge of the app, more elements to load is requested.
 *
 * @param experience - Experience instance
 */
function requestMoreData(experience) {
    sendSdkMessage(experience, Action.OnMoreDataRequested);
}
/**
 * @internal
 */
function registerEmbedHook(hook) {
    embedHooks.push(hook);
}
function enableLogging(logLevelOrFlag = true) {
    if (typeof logLevelOrFlag === 'boolean') {
        logger.logLevel = logLevelOrFlag ? LogLevel.Verbose : LogLevel.Silent;
        return;
    }
    logger.logLevel = logLevelOrFlag;
}
registerEmbedHook(onResize);

/**
 * @license
 * parent_application_impl.ts
 * launcher-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-07-12
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
class ParentApplicationImpl {
    constructor(bridge) {
        this.bridge = bridge;
    }
}

/**
 * @license
 * parent_application.ts
 * launcher-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2023-02-15
 * Copyright © 2023 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
let parentApplication;
/**
 * Returns instance of parent application.
 *
 * @returns Returns {@link ParentApplication}
 * if an Experience resides within the parent application, and it was embedded
 * using {@link embed} function. Otherwise it returns `null`.
 */
function getParentApplication() {
    if (parentApplication !== undefined) {
        return parentApplication;
    }
    const parentBridge = getParentBridge();
    if (parentBridge === null) {
        return null;
    }
    parentApplication = new ParentApplicationImpl(parentBridge);
    return parentApplication;
}

/**
 * @license
 * child_helpers.ts
 * launcher-kit
 *
 * Created by Josep Rodriguez <josep.rodriguez@monterosa.co.uk> on 2022-07-13
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
function getUrlParam(param) {
    let queryString = {};
    if (typeof window !== 'undefined') {
        queryString = window.location.search;
    }
    const urlParams = new URLSearchParams(queryString);
    return urlParams.get(param);
}
/**
 * The SDK on the parent application will by default request a child
 * Experience to hide its header and footer by setting a query parameter called
 * `micHideHeaderAndFooter` to `1`.
 *
 * This function will return true if that is the case, and false otherwise.
 *
 * When true, the developer of a child Experience is expected to hide it's headers
 * and footers as the parent application is actively providing them.
 *
 * You can test whether you are respecting this correctly by adding the following query
 * to your URL:
 *
 * "?micHideHeaderAndFooter=1"
 *
 * @returns Whether the header and footer views should be hidden.
 */
function shouldHideHeaderAndFooter() {
    return getUrlParam(QueryParam.HideHeaderAndFooter) === '1';
}
/**
 * This function will returns true if experience is embedded in autoresize height mode.
 */
function isAutoresizesHeight() {
    return getUrlParam(QueryParam.AutoresizesHeight) === '1';
}
/**
 * This function is used to notify the parent application that the Experience
 * is ready to be used. It's intended to be called by the Experience itself and
 * not by the parent application.
 *
 * @example
 * ```javascript
 * import { sendReady } from '@monterosa-sdk/launcher-kit';
 *
 * sendReady();
 * ```
 *
 * @returns void
 */
function sendReady() {
    const parentApp = getParentApplication();
    if (parentApp === null) {
        console.log('Unable to send finished loading UI message, as there is no parent application');
        return;
    }
    sendSdkMessage(parentApp, Action.OnReady);
}
/**
 * Sends a message to the SDK of the parent application informing it that
 * the UI of this Experience has completed loading.
 *
 * You should call this method when your UI has completed loading and you are ready to
 * display data to the user. This can be when your network calls have successfully requested
 * data and you have updated the DOM for it, or when an error has occurred and you want
 * to take over the UI to provide details to the user.
 *
 * The SDK will use this message to dismiss the loading indicator, allowing a seamless
 * transition between a native loading UI, into a fully loaded, child Experience.
 *
 * If this call is not made, the parent SDK will add a grace period to ensure the web app
 * has sufficient time to load, and eventually dismiss the loading state, whether Experience
 * is ready or not, so as to avoid scenarios were a defect leaves an infinite loading state
 * presented to the user.
 */
function sendFinishedLoadingUI() {
    const parentApp = getParentApplication();
    if (parentApp === null) {
        console.log('Unable to send finished loading UI message, as there is no parent application');
        return;
    }
    sendSdkMessage(parentApp, Action.OnUILoaded);
}
/**
 * Adds an observer for when more data is requested by the parent application
 *
 * @param callback - The callback that is triggered when a request for more data
 * is received
 * @returns The unsubscribe function. When it's called,
 * the observer will be removed and requests for data will no longer be received
 */
function onMoreDataRequested(callback) {
    const parentApp = getParentApplication();
    if (parentApp === null) {
        console.log('Unable to subscribe to more data event, as there is no parent application');
        return () => { };
    }
    return onSdkMessage(parentApp, ({ action }) => {
        if (action === Action.OnMoreDataRequested) {
            callback();
        }
    });
}
/**
 * Adds an observer for when experience is fully embedded and ready to accept
 * incoming messages
 *
 * @param experience - Experience instance
 * @param callback - The callback that is triggered when experience is embedded
 * @returns The unsubscribe function. When it's called, the observer will be
 * removed and ready event will not be received
 */
function onReady(experience, callback) {
    return onSdkMessage(experience, ({ action }) => {
        if (action === Action.OnReady) {
            callback();
        }
    });
}
/**
 * @internal
 */
const sendExperienceSizeThrottled = throttle((width, height) => {
    const parentApp = getParentApplication();
    if (parentApp === null)
        return;
    sendSdkMessage(parentApp, Action.OnResize, { width, height });
}, RESIZE_THROTTLE_TIMEOUT, {
    leading: true,
    trailing: true,
});
/**
 * @internal
 */
function sendExperienceSize(width, height) {
    return sendExperienceSizeThrottled(width, height);
}
/**
 * Sets up an observer to watch for the element dimensions change and
 * automatically reports their changes to the parent application.
 *
 * @param element - HTML element whose dimensions need to be watched
 * @returns The unsubscribe function. When it's called,
 * the observer will be removed and element dimensions are no longer tracked
 */
function reportExperienceSizeChanges(element) {
    const observer = new ResizeObserver(() => {
        const width = Math.max(element.offsetWidth, element.scrollWidth);
        const height = Math.max(element.offsetHeight, element.scrollHeight);
        sendExperienceSize(width, height);
    });
    observer.observe(element);
    return () => observer.unobserve(element);
}

/**
 * @license
 * storage_impl.ts
 * launcher-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2023-02-15
 * Copyright © 2023 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
class StorageImpl {
    constructor() {
        this.memoryStore = {};
        this.accessible = checkAvailability();
        this._persistent = false;
    }
    set persistent(newValue) {
        const oldValue = this._persistent;
        if (oldValue === newValue) {
            return;
        }
        const swapToStorage = newValue === true && this.accessible;
        const swapToMemory = newValue === false && this.accessible;
        if (swapToStorage) {
            for (const [key, value] of Object.entries(this.memoryStore)) {
                setItem(key, value);
            }
        }
        if (swapToMemory) {
            clear();
        }
        this._persistent = newValue;
    }
    get persistent() {
        return this._persistent;
    }
    getItem(key) {
        if (this.persistent && this.accessible) {
            return getItem(key);
        }
        if (Object.prototype.hasOwnProperty.call(this.memoryStore, key)) {
            return this.memoryStore[key];
        }
        return null;
    }
    setItem(key, value) {
        if (this.persistent && this.accessible) {
            setItem(key, value);
        }
        this.memoryStore[key] = value;
    }
    removeItem(key) {
        if (this.persistent && this.accessible) {
            removeItem(key);
        }
        delete this.memoryStore[key];
    }
    clear() {
        if (this.persistent && this.accessible) {
            clear();
        }
        this.memoryStore = {};
    }
}

/**
 * @license
 * storage.ts
 * launcher-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2023-02-15
 * Copyright © 2023 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * @internal
 */
var StorageAction;
(function (StorageAction) {
    StorageAction["Read"] = "storageRead";
    StorageAction["Write"] = "storageWrite";
    StorageAction["Remove"] = "storageRemove";
    StorageAction["Clear"] = "storageClear";
})(StorageAction || (StorageAction = {}));
/**
 * @internal
 */
const storage = new StorageImpl();
/**
 * @internal
 */
function listenStorageMessages(experience) {
    return onSdkMessage(experience, (message) => {
        let payload = {};
        if (!Object.values(StorageAction).includes(message.action)) {
            return;
        }
        switch (message.action) {
            case StorageAction.Read:
                payload = {
                    value: storage.getItem(message.payload.key),
                };
                break;
            case StorageAction.Write:
                storage.setItem(message.payload.key, message.payload.value);
                break;
            case StorageAction.Remove:
                storage.removeItem(message.payload.key);
                break;
            case StorageAction.Clear:
                storage.clear();
                break;
        }
        respondToSdkMessage(experience, message, payload);
    });
}
/**
 * The `setStoragePersistent` function is a simple function that allows to
 * control the persistence of the SDK storage. If the argument `persistent` is
 * set to `true`, then the storage will be persistent across browser sessions
 * and if it is set to `false`, then the storage will save to memory.
 * It is  important to note that the use of persistent storage may be subject
 * to laws and regulations, such as those related to data privacy and protection.
 *
 * @remarks
 * - We transition from persistent to memory and memory to persistent in
 *   a seamless manner for you
 *
 * - By default we store in memory
 *
 * - The value of storage persistent persists across session (aka put it to true,
 *   it will remain so, put it back to false, it will remain so)
 *
 * - You have the responsibility to comply with any laws and regulations, and
 *   store only the data if you know it's valid to do so
 *
 * @param persistent - Determines whether or not SDK storage should persist
 * across browser sessions.
 */
function setStoragePersistent(persistent) {
    storage.persistent = persistent;
}
/**
 * The function allows to read data from the SDK storage.
 *
 * @param key - The name of the item to be read from storage.
 *
 * @throws
 * The function throws an error if there is a timeout reading the data
 * from the parent application storage.
 *
 * @returns A promise that resolves to the value
 * of the item in storage or `null` if the item doesn't exist.
 */
async function storageRead(key) {
    const parentApp = getParentApplication();
    let value = null;
    if (parentApp !== null) {
        const result = await sendSdkRequest(parentApp, StorageAction.Read, {
            key,
        });
        value = result.payload.value;
    }
    else {
        value = storage.getItem(key);
    }
    return Promise.resolve(value);
}
/**
 * The function allows to write data to the SDK storage.
 *
 * @param key - A name of the item to be stored.
 * @param value - A value to be stored.
 *
 * @throws
 * The function throws an error if there is a timeout writing the data
 * to the parent's application storage.
 *
 * @returns A promise that resolves to `void` once the data has
 * been successfully stored.
 */
async function storageWrite(key, value) {
    const parentApp = getParentApplication();
    if (parentApp !== null) {
        await sendSdkRequest(parentApp, StorageAction.Write, {
            key,
            value,
        });
    }
    else {
        storage.setItem(key, value);
    }
    return Promise.resolve();
}
/**
 * The function allows to remove an item from the SDK storage.
 *
 * @param key - A name of the item to be removed.
 *
 * @throws
 * The function throws an error if there is a timeout removing the data
 * from the parent's application storage.
 *
 * @returns A promise that resolves to `void` once the data has
 * been successfully removed.
 */
async function storageRemove(key) {
    const parentApp = getParentApplication();
    if (parentApp !== null) {
        await sendSdkRequest(parentApp, StorageAction.Remove, { key });
    }
    else {
        storage.removeItem(key);
    }
    return Promise.resolve();
}
/**
 * The function allows to clear all data from the SDK storage.
 *
 * @throws
 * The function throws an error if there is a timeout clearing the parent's
 * application storage.
 *
 * @returns A promise that resolves to `void` once the data has been
 * successfully cleared.
 */
async function storageClear() {
    const parentApp = getParentApplication();
    if (parentApp !== null) {
        await sendSdkRequest(parentApp, StorageAction.Clear);
    }
    else {
        storage.clear();
    }
    return Promise.resolve();
}
registerEmbedHook(listenStorageMessages);

export { Action, VERSION$1 as BRIDGE_VERSION, BridgeError, BridgeImpl, QueryParam, Source, StorageAction, VERSION, embed, enableLogging, getBridge, getExperience, getParentApplication, getParentBridge, isAutoresizesHeight, listenStorageMessages, onMessage, onMoreDataRequested, onReady, onSdkMessage, registerEmbedHook, reportExperienceSizeChanges, requestMoreData, respondToMessage, respondToSdkMessage, sendExperienceSize, sendExperienceSizeThrottled, sendFinishedLoadingUI, sendMessage, sendReady, sendRequest, sendSdkMessage, sendSdkRequest, setRequestTimeout, setStoragePersistent, shouldHideHeaderAndFooter, storage, storageClear, storageRead, storageRemove, storageWrite, unmount };
//# sourceMappingURL=index.esm2017.js.map
